import { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import digitalServicesIcon from 'assets/digitalService.svg';
import pageTemplatesIcon from 'assets/pageTemplates.svg';
import serviceChecklistIcon from 'assets/serviceChecklist.svg';
import spacesServices from 'assets/spacesServices.svg';
import checkMark from 'assets/checkMark.svg';

// Category constants
const CATEGORIES = {
  TAMM_SPACES_SERVICES: 'TAMM Spaces Services Onboarding Guidelines',
  DIGITAL_SERVICE_USABILITY: 'Digital Service Usability & Accessibility Standards',
  ADGES_SERVICE_CHECKLIST: 'ADGEs Service Checklist',
  TAMM_PAGE_TEMPLATES: 'TAMM Page Templates',
};

const ReviewCompleted = ({ reviewData }) => {
  const processedData = useMemo(() => processReviewData(reviewData), [reviewData]);

  // Show loading or empty state if no processed data
  if (!processedData) {
    return (
      <div className="flex flex-col gap-24 w-full flex-1">
        <div className="text-center text-gray-500">
          Loading review data...
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-24 w-full flex-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-16">

          {/* Title and Details */}
          <div className="flex flex-col gap-8">
            <h3 className="text-md 2xl:text-lg font-medium text-gray-900">
              {processedData.title}
            </h3>
            <p className="flex sm:text-xs 2xl:text-sm text-gray-500">
              {processedData.totalFindings}
              {' '}
              findings
              <div className="mx-8 w-[1px] bg-gray-200" />
              {processedData.reviewType}
            </p>
          </div>
        </div>
      </div>

      {/* Categories List */}
      <div className="flex flex-col gap-12">
        {processedData.categories.map(category => (
          <div
            key={category.id}
            className="flex items-center justify-between p-3 2xl:p-4 border border-gray-200 rounded-lg
            hover:bg-gray-50 transition-colors"
            style={{
              boxShadow: '0 2px 8px -2px rgba(0, 0, 0, 0.08), 0 1px 2px 0 rgba(0, 0, 0, 0.04)',
            }}
          >
            <div className="flex items-center gap-16">
              <span className="text-md 2xl:text-lg">
                <img src={category.icon} alt={category.title} />
              </span>
              <span
                className={cn(
                  'sm:text-xs 2xl:text-sm font-medium',
                  {
                    'text-primary-500': category.findings === 0,
                    'text-gray-900': category.findings !== 0,
                  }
                )}
              >
                {category.title}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {category.findings === 0
                ? (
                  <div className="flex items-center justify-center w-6 h-6 rounded-full">
                    <span className="text-green-600 text-md">
                      <img src={checkMark} alt="" />
                    </span>
                  </div>
                )
                : (
                  <div
                    className={cn(
                      'flex items-center gap-4',
                      getStatusColor(category.findings)
                    )}
                  >
                    <span className="text-xs">
                      {category.findings}
                    </span>
                    <span className="text-xs">
                      findings
                    </span>
                  </div>
                )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  function getStatusColor(findings) {
    if (findings === 0) {
      return 'text-primary-500';
    }
    if (findings < 5) {
      return 'text-yellow-600';
    }

    return 'text-red-500';
  }

  // Process the actual review data
  function processReviewData(data) {
    if (!data || !data.feedbacks) {
      return null;
    }

    // Group feedbacks by design_quality_source
    const groupedFeedbacks = data.feedbacks.reduce((acc, feedback) => {
      const source = feedback.design_quality_source;

      if (!acc[source]) {
        acc[source] = 0;
      }
      acc[source]++;

      return acc;
    }, {});

    // Define category icons mapping
    const categoryIcons = {
      [CATEGORIES.TAMM_SPACES_SERVICES]: spacesServices,
      [CATEGORIES.DIGITAL_SERVICE_USABILITY]: digitalServicesIcon,
      [CATEGORIES.ADGES_SERVICE_CHECKLIST]: serviceChecklistIcon,
      [CATEGORIES.TAMM_PAGE_TEMPLATES]: pageTemplatesIcon,
    };

    // Create categories array with all possible categories
    const allCategories = [
      CATEGORIES.TAMM_SPACES_SERVICES,
      CATEGORIES.DIGITAL_SERVICE_USABILITY,
      CATEGORIES.ADGES_SERVICE_CHECKLIST,
      CATEGORIES.TAMM_PAGE_TEMPLATES,
    ];

    const categories = allCategories.map((categoryName, index) => ({
      id: index + 1,
      icon: categoryIcons[categoryName] || digitalServicesIcon,
      title: categoryName,
      findings: groupedFeedbacks[categoryName] || 0,
    }));

    // Filter to show only categories that appear in the data,
    // but ensure we always show the key categories. If audit data explicitly
    // indicates `has_tamm_guideline === false`, do not treat TAMM Spaces Services
    // as a key category; otherwise leave the default key categories intact.
    let keyCategories = [
      CATEGORIES.DIGITAL_SERVICE_USABILITY,
      CATEGORIES.TAMM_SPACES_SERVICES,
    ];

    if (
      data &&
      data.audit_data &&
      Object.prototype.hasOwnProperty.call(data.audit_data, 'has_tamm_guideline') &&
      data.audit_data.has_tamm_guideline === false
    ) {
      keyCategories = keyCategories.filter(
        cat => cat !== CATEGORIES.TAMM_SPACES_SERVICES
      );
    }

    const categoriesToShow = categories.filter(
      cat => groupedFeedbacks[cat.title] > 0 || keyCategories.includes(cat.title)
    );

    let reviewType = 'Design Review';

    if (data.customer && data.customer.name) {
      reviewType = `${data.customer.name} Review`;
    }

    return {
      title: data.title,
      totalFindings: data.feedbacks.length,
      reviewType: reviewType,
      categories: categoriesToShow,
    };
  }
};

ReviewCompleted.propTypes = {
  reviewData: PropTypes.object,
};

export default ReviewCompleted;
